/**
 * Pick the Google Ads conversion action to upload conversions to.
 *
 * Only UPLOAD_CLICKS-type actions are eligible — that's the same type that
 * hosts both standard offline uploads and ECL. WEBPAGE actions (which the
 * existing setup wizard creates) won't appear in this list.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { offlineConversionsApi } from '@/services/api/offlineConversions';
import type { ConversionActionSummary } from '@/services/api/offlineConversions';

interface Props {
  googleAdsAccountId: string;
  selectedId: string | null;
  onSelect: (action: ConversionActionSummary) => void;
}

export const ChooseConversionActionStep: React.FC<Props> = ({
  googleAdsAccountId,
  selectedId,
  onSelect,
}) => {
  const query = useQuery({
    queryKey: ['offline-conversions-actions', googleAdsAccountId],
    queryFn: () => offlineConversionsApi.listEligibleActions(googleAdsAccountId),
    refetchOnWindowFocus: false,
  });

  if (query.isLoading) {
    return <p className="text-sm text-gray-600">Loading conversion actions…</p>;
  }
  if (query.isError) {
    return (
      <Alert variant="error">
        Failed to load conversion actions.{' '}
        <Button variant="ghost" onClick={() => query.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  const actions = query.data!;

  if (actions.length === 0) {
    return (
      <Alert variant="warning">
        <p>
          No conversion actions of type <strong>Import from clicks</strong>{' '}
          (UPLOAD_CLICKS) were found on this Google Ads account.
        </p>
        <p className="mt-1 text-sm">
          Create one at <em>Google Ads → Goals → New conversion action →
          Import → Other data sources or CRMs → Track conversions from clicks</em>.
          Then come back and click <Button variant="ghost" onClick={() => query.refetch()}>Refresh</Button>.
        </p>
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600">
        Pick the conversion action that will receive these uploads. Only
        actions of type <em>Import from clicks</em> are eligible.
      </p>
      <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
        {actions.map((a) => {
          const isSelected = selectedId === a.id;
          return (
            <li
              key={a.id}
              className={`flex items-center justify-between p-3 ${
                isSelected ? 'bg-blue-50' : ''
              }`}
            >
              <div>
                <p className="font-medium text-gray-900">{a.name}</p>
                <p className="text-xs text-gray-500">
                  ID {a.id} · category {a.category ?? 'unspecified'}
                </p>
              </div>
              <Button
                variant={isSelected ? 'success' : 'secondary'}
                onClick={() => onSelect(a)}
              >
                {isSelected ? 'Selected' : 'Choose'}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
